import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon
} from '@opengov/react-capital-assets';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IStoriesFolder } from '../../../../types';
import { styles } from './FolderTreeSelect.styles';

export type IFolderTreeSelectProps = {
  data: IStoriesFolder[];
  activeNodeId: string;
  onNodeSelect(node: IStoriesFolder): void;
  disabled?: boolean;
  showDisabledStyle?: boolean;
  showFilter?: boolean;
  filterPlaceholder?: string;
  maxMenuHeight?: number;
  dataTest?: string;
};

const findNode = (nodes: IStoriesFolder[], id: string): IStoriesFolder | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children ?? [], id);
    if (found) return found;
  }
  return null;
};

const matchesFilter = (node: IStoriesFolder, filter: string): boolean => {
  if (node.displayName.toLowerCase().includes(filter)) return true;
  return (node.children ?? []).some((child) => matchesFilter(child, filter));
};

const filterTree = (nodes: IStoriesFolder[], filter: string): IStoriesFolder[] => {
  if (!filter) return nodes;
  return nodes
    .filter((node) => matchesFilter(node, filter))
    .map((node) => {
      const selfMatches = node.displayName.toLowerCase().includes(filter);
      return {
        ...node,
        children: selfMatches ? (node.children ?? []) : filterTree(node.children ?? [], filter)
      };
    });
};

const collectParentIds = (nodes: IStoriesFolder[]): string[] =>
  nodes.flatMap((node) => {
    const children = node.children ?? [];
    return children.length > 0 ? [node.id, ...collectParentIds(children)] : [];
  });

// Collects ids of nodes whose data marks them as pre-expanded (toggled)
const collectToggledIds = (nodes: IStoriesFolder[]): string[] => {
  const ids: string[] = [];
  nodes.forEach((node) => {
    if (node.toggled) ids.push(node.id);
    ids.push(...collectToggledIds(node.children ?? []));
  });
  return ids;
};

// Flattens only visible (expanded) nodes for keyboard navigation
const flattenVisible = (nodes: IStoriesFolder[], expandedIds: Set<string>): IStoriesFolder[] =>
  nodes.flatMap((node) => {
    const children = expandedIds.has(node.id) ? flattenVisible(node.children ?? [], expandedIds) : [];
    return [node, ...children];
  });

type ITreeNodeProps = {
  node: IStoriesFolder;
  activeNodeId: string;
  focusedNodeId: string | null;
  expandedIds: Set<string>;
  onToggle(id: string): void;
  onSelect(node: IStoriesFolder): void;
  depth: number;
  showDisabledStyle: boolean;
};

const TreeNode = ({ node, activeNodeId, focusedNodeId, expandedIds, onToggle, onSelect, depth, showDisabledStyle }: ITreeNodeProps) => {
  const hasChildren = (node.children ?? []).length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = node.id === activeNodeId;
  const isFocused = node.id === focusedNodeId;
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocused && nodeRef.current && document.activeElement?.tagName !== 'INPUT') {
      nodeRef.current.focus();
    }
  }, [isFocused]);

  const button = (
    <ListItemButton
      ref={nodeRef}
      selected={isSelected}
      tabIndex={isFocused ? 0 : -1}
      onClick={() => {
        if (!node.disabled) {
          onSelect(node);
        }
      }}
      sx={{ ...styles.nodeButton, ...(node.disabled && showDisabledStyle && styles.nodeButtonDisabled), paddingLeft: `${8 + depth * 16}px` }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' && hasChildren && !isExpanded) {
          e.preventDefault();
          onToggle(node.id);
        } else if (e.key === 'ArrowLeft' && hasChildren && isExpanded) {
          e.preventDefault();
          onToggle(node.id);
        }
      }}
      data-test={`tree-node-${node.id}`}
    >
      {hasChildren ? (
        <SvgIcon
          component={isExpanded ? ChevronDownIcon : ChevronRightIcon}
          inheritViewBox
          sx={styles.toggleIcon}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.id);
          }}
        />
      ) : (
        <Box sx={styles.toggleIconPlaceholder} />
      )}
      <ListItemText
        primary={node.displayName}
        primaryTypographyProps={{ sx: styles.nodeLabel }}
        data-test={`tree-node-label-${node.id}`}
      />
      {node.disabled && node.tooltip && (
        <Typography sx={styles.disabledLabel}>{node.tooltip}</Typography>
      )}
    </ListItemButton>
  );

  return (
    <>
      <Tooltip title={node.tooltip ?? node.displayName} placement="bottom" enterDelay={500}>
        {button}
      </Tooltip>
      {hasChildren && (
        <Collapse in={isExpanded} unmountOnExit>
          <List disablePadding>
            {(node.children ?? []).map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                activeNodeId={activeNodeId}
                focusedNodeId={focusedNodeId}
                expandedIds={expandedIds}
                onToggle={onToggle}
                onSelect={onSelect}
                depth={depth + 1}
                showDisabledStyle={showDisabledStyle}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const FolderTreeSelect = ({
  data,
  activeNodeId,
  onNodeSelect,
  disabled = false,
  showDisabledStyle = true,
  showFilter = false,
  filterPlaceholder = 'Select...',
  maxMenuHeight = 300,
  dataTest
}: IFolderTreeSelectProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(collectToggledIds(data ?? [])));
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  const open = Boolean(anchorEl);
  const activeNode = useMemo(() => findNode(data ?? [], activeNodeId), [data, activeNodeId]);
  const filteredData = useMemo(() => filterTree(data ?? [], filter.toLowerCase()), [data, filter]);
  const visibleNodes = useMemo(() => flattenVisible(filteredData, expandedIds), [filteredData, expandedIds]);

  useEffect(() => {
    if (filter) {
      setExpandedIds(new Set(collectParentIds(filteredData)));
    } else {
      setExpandedIds(new Set());
    }
  }, [filter, filteredData]);

  // Re-seed pre-expanded (toggled) sections when the data set changes, so
  // section children (stories) are mounted on open without manual expansion.
  useEffect(() => {
    setExpandedIds((prev) => new Set([...prev, ...collectToggledIds(data ?? [])]));
  }, [data]);

  const onToggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleOpen = () => {
    if (!disabled) {
      setAnchorEl(triggerRef.current);
      setFocusedNodeId(activeNodeId ?? visibleNodes[0]?.id ?? null);
    }
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setFilter('');
    setFocusedNodeId(null);
    triggerRef.current?.focus();
  }, []);

  const handleSelect = (node: IStoriesFolder) => {
    onNodeSelect(node);
    handleClose();
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }

    if ((e.target as HTMLElement).tagName === 'INPUT') return;

    const selectableNodes = visibleNodes.filter((n) => !n.disabled);
    const currentIndex = selectableNodes.findIndex((n) => n.id === focusedNodeId);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = selectableNodes[currentIndex + 1] ?? selectableNodes[0];
      if (next) setFocusedNodeId(next.id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = selectableNodes[currentIndex - 1] ?? selectableNodes[selectableNodes.length - 1];
      if (prev) setFocusedNodeId(prev.id);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const focused = visibleNodes.find((n) => n.id === focusedNodeId);
      if (focused && !focused.disabled && !(focused.children ?? []).length) handleSelect(focused);
    }
  };

  return (
    <>
      <Box
        ref={triggerRef}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-label={activeNode ? activeNode.displayName : filterPlaceholder}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        sx={styles.trigger}
        data-test={dataTest}
      >
        <Typography sx={styles.triggerLabel} color={activeNode ? 'text.primary' : 'text.secondary'}>
          {activeNode ? activeNode.displayName : filterPlaceholder}
        </Typography>
        <SvgIcon
          component={open ? ChevronDownIcon : ChevronRightIcon}
          inheritViewBox
          sx={styles.triggerIcon}
          aria-hidden={true}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onKeyDown={handlePopoverKeyDown}
        marginThreshold={8}
        PaperProps={{
          sx: {
            ...styles.popoverPaper,
            width: triggerRef.current ? triggerRef.current.offsetWidth : 'auto',
            maxHeight: `min(${maxMenuHeight}px, calc(100vh - 80px))`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }}
        disableAutoFocus={showFilter}
      >
        {showFilter && (
          <Box sx={styles.filterBox}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={filterPlaceholder}
              aria-label={filterPlaceholder}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              inputProps={{ className: 'no-format' }}
            />
          </Box>
        )}
        <Box sx={styles.treeBox}>
          <List disablePadding role="listbox">
            {filteredData.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                activeNodeId={activeNodeId}
                focusedNodeId={focusedNodeId}
                expandedIds={expandedIds}
                onToggle={onToggle}
                onSelect={handleSelect}
                depth={0}
                showDisabledStyle={showDisabledStyle}
              />
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};
